import {
  Component,
  DestroyRef,
  effect,
  inject,
  signal,
  computed,
  Type,
  ViewChild,
  ElementRef,
  HostListener,
  OnDestroy,
  AfterViewInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Tooltip } from 'primeng/tooltip';
import { ScrollRegionComponent } from '@brightside/angular-bds';
import { debounceTime, Subject } from 'rxjs';
import { OmniSearchService } from '../omni-search.service';
import {
  OMNI_SEARCH_PORTALED_OVERLAY_SELECTOR,
  OmniSearchHistoryEntry,
  OmniSearchProvider,
  OmniSearchResult,
} from '../models/omni-search.models';
import { AmplitudeService } from '@brighthub/logging';

@Component({
  selector: 'core-search-omni-search',
  standalone: true,
  imports: [CommonModule, FormsModule, Tooltip, ScrollRegionComponent],
  templateUrl: './omni-search.component.html',
  styleUrl: './omni-search.component.scss',
})
export class OmniSearchComponent implements OnDestroy, AfterViewInit {
  private _search = inject(OmniSearchService);
  private _destroyRef = inject(DestroyRef);
  private _input$ = new Subject<string>();
  private _amplitude = inject(AmplitudeService);
  private _onDocumentClick = (e: MouseEvent) => this._handleDocumentClick(e);
  // Ref to the input (add #searchInput on the <input> in your template)
  @ViewChild('searchInput', { static: false })
  searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('omniSearchRoot', { static: false })
  omniSearchRoot?: ElementRef<HTMLElement>;
  @ViewChild('paletteContainer', { static: false })
  paletteContainer?: ElementRef<HTMLElement>;
  // --- Signals ---
  query = signal('');
  showPalette = signal(false);
  showSuggestions = signal(false);
  results = signal<OmniSearchResult[] | null>(null);
  activeProvider = signal<OmniSearchProvider | undefined>(undefined);
  activeComponent = signal<Type<any> | null>(null);
  /** True only while exactly one RBAC-visible provider exists — derived, never stale from async races. */
  readonly singleProvider = computed(() => this._search.providers().length === 1);
  suggestedProviders = signal<OmniSearchProvider[]>([]);
  loading = signal(false);
  /** When set, matches the history entry whose cached results are currently shown. */
  historyReplayEntryId = signal<string | null>(null);
  /** True while the search input has focus; recent searches require focus to dismiss on outside click. */
  private readonly _inputFocused = signal(false);
  /** Bumps to cancel in-flight single-provider auto-select when the provider list changes. */
  private _autoSelectGeneration = 0;
  /** True when the current chip was auto-selected because only one provider was visible. */
  private _autoSelectedSingle = false;

  readonly recentHistoryEntries = computed(() => {
    return this.activeProvider()?.searchHistory?.entries() ?? [];
  });

  /**
   * Show while the search input is focused, no search result list is displayed yet (`results === null`),
   * and the active provider has history entries. Recents hide on blur/outside click and when a search
   * completes (non-null results, including `[]`).
   */
  readonly showRecentSearches = computed(() => {
    if (!this._inputFocused()) {
      return false;
    }
    const p = this.activeProvider();
    if (!p?.searchHistory) {
      return false;
    }
    if (this.loading()) {
      return false;
    }
    if (this.results() !== null) {
      return false;
    }
    return p.searchHistory.entries().length > 0;
  });

  /** Omni results palette: "N result(s)" — any provider. */
  omniResultsCountLabel(count: number): string {
    const n = Math.max(0, Math.floor(Number(count)) || 0);
    return `${n} ${n === 1 ? 'result' : 'results'}`;
  }

  /** History row hit count (stored or cached row length). */
  historyResultCountLabel(entry: OmniSearchHistoryEntry): string {
    return this.omniResultsCountLabel(entry.resultCount ?? entry.results.length);
  }

  // Auto-computed from service.providers()
  groupedProviders = computed(() => {
    const providers = this._search.providers(); // <-- reactive signal
    const grouped: { subApp: string; providers: OmniSearchProvider[] }[] = [];
    for (const p of providers) {
      const existing = grouped.find((g) => g.subApp === p.subApp);
      if (existing) existing.providers.push(p);
      else grouped.push({ subApp: p.subApp, providers: [p] });
    }
    return grouped;
  });

  _absoluteIndex(groupIndex: number, providerIndex: number): number {
    const groups = this.groupedProviders();
    let offset = 0;
    for (let i = 0; i < groupIndex; i++) {
      offset += groups[i].providers.length;
    }
    return offset + providerIndex;
  }
  highlightedProviderIndex = signal<number>(-1); // -1 means none
  highlightedIndex = signal<number>(-1);

  @HostListener('window:keydown', ['$event'])
  onKeydown(e: KeyboardEvent) {
    const query = this.query();
    if (e.key === 'Escape' && this._isOmniSearchActive()) {
      this._dismissOmniSearchOnEscape();
      e.stopPropagation();
      e.preventDefault();
      return;
    }

    // --- navigation for palette or suggestions ---
    const list =
      this.showSuggestions() && this.suggestedProviders().length
        ? this.suggestedProviders()
        : this.showPalette()
          ? this._flattenedProviders()
          : [];

    // If palette or suggestions visible → handle navigation
    if (list.length) {
      if (['ArrowDown', 'ArrowUp', 'Tab', 'Enter'].includes(e.key)) {
        e.preventDefault();
        let newIndex = this.highlightedIndex();

        if (e.key === 'ArrowDown') {
          newIndex = (newIndex + 1) % list.length;
        } else if (e.key === 'ArrowUp') {
          newIndex = newIndex <= 0 ? list.length - 1 : newIndex - 1;
        }

        // select with Tab or Enter
        if ((e.key === 'Tab' || e.key === 'Enter') && list.length) {
          e.preventDefault();

          let index = this.highlightedIndex();
          if (index < 0) index = 0; // fallback to first suggestion

          const item = list[index];
          if (item) {
            this.selectProvider(item, query.startsWith('@'));
            this.highlightedIndex.set(-1);
          }
          return;
        }


        this.highlightedIndex.set(newIndex);
        this._scrollIntoView(newIndex);
        return;
      }
    }

    if (
      e.key === 'Enter' &&
      !this.showPalette() &&
      !this.showSuggestions() &&
      this.activeProvider() &&
      this.query().trim()
    ) {
      e.preventDefault();
      this.onSearch();
    }
  }


// --- flatten providers for palette ---
  private _flattenedProviders(): OmniSearchProvider[] {
    return this.groupedProviders()
      .map((g) => g.providers)
      .reduce((acc, val) => acc.concat(val), []);
  }

// --- scroll the focused item into view ---
  private _scrollIntoView(index: number) {
    const container =
      this.showSuggestions()
        ? document.querySelector('.omni-dropdown')
        : this.paletteContainer?.nativeElement;

    if (!container) return;
    const items = container.querySelectorAll('.palette-item, .dropdown-item');
    const el = items[index] as HTMLElement;
    el?.scrollIntoView({ block: 'nearest' });
  }

  constructor() {
    effect(() => {
      const providers = this._search.providers();
      const generation = ++this._autoSelectGeneration;

      // Multi-provider: drop a stale single-provider auto-lock so the palette returns.
      if (providers.length > 1 && this._autoSelectedSingle) {
        this._autoSelectedSingle = false;
        this.activeProvider.set(undefined);
        this.activeComponent.set(null);
        this.historyReplayEntryId.set(null);
        this.results.set(null);
        return;
      }

      if (providers.length !== 1) {
        return;
      }
      if (this.activeProvider()) {
        return;
      }

      const p = providers[0];
      void (async () => {
        // Select synchronously so outside-click → clear → re-lock stays snappy;
        // still guard after await in case the provider list grew meanwhile.
        if (generation !== this._autoSelectGeneration) {
          return;
        }
        if (this._search.providers().length !== 1 || this.activeProvider()) {
          return;
        }
        this.historyReplayEntryId.set(null);
        this._autoSelectedSingle = true;
        this.activeProvider.set(p);
        this.showPalette.set(false);
        this._afterProviderSelected();

        const component = await p.loadComponent();
        if (generation !== this._autoSelectGeneration) {
          return;
        }
        if (this._search.providers().length !== 1 || this.activeProvider()?.id !== p.id) {
          return;
        }
        this.activeComponent.set(component);
      })();
    });

    this._search.requestClearOmniUi$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this.clearSearch());

    // Debounced input handler
    this._input$
      .pipe(debounceTime(150), takeUntilDestroyed(this._destroyRef))
      .subscribe((q) => this._handleInput(q));
  }

  ngAfterViewInit() {
    // attach global click listener once view ready
    document.addEventListener('click', this._onDocumentClick, true);
  }

  ngOnDestroy() {
    document.removeEventListener('click', this._onDocumentClick, true);
  }

  // --- UI Event Handlers ---

  onFocus() {
    this._inputFocused.set(true);
    if (!this.activeProvider()) this.showPalette.set(true);
  }

  /**
   * Clears focus state when the user leaves the search input, except when focus moves to another
   * control inside the omni bar (history row, provider palette, etc.). Without that guard, blur runs
   * before the click and `@if (showRecentSearches())` removes the history DOM so `applyHistoryEntry`
   * never runs.
   */
  onInputBlur(ev: FocusEvent) {
    const root = this.omniSearchRoot?.nativeElement;
    const related = ev.relatedTarget;
    if (root && related instanceof Node && root.contains(related)) {
      return;
    }
    queueMicrotask(() => {
      const active = document.activeElement;
      if (root && active instanceof Node && root.contains(active)) {
        return;
      }
      this._inputFocused.set(false);
    });
  }

  onInputChange() {
    this._input$.next(this.query());
  }

  async selectProvider(p: OmniSearchProvider, clear = false) {
    this._autoSelectedSingle = false;
    this.historyReplayEntryId.set(null);
    this.activeProvider.set(p);
    this.activeComponent.set(await p.loadComponent());
    this.showPalette.set(false);
    this.showSuggestions.set(false);
    this._focusInputSoon(); // refocus so user can keep typing
    if (clear) {
      this.query.set('');
    }
    this._afterProviderSelected();
  }

  clearProvider() {
    this._autoSelectedSingle = false;
    this.historyReplayEntryId.set(null);
    this.activeProvider.set(undefined);
    this.activeComponent.set(null);
    this.results.set(null);
    this._focusInputSoon();
  }

  clearSearch() {
    this._autoSelectedSingle = false;
    this.historyReplayEntryId.set(null);
    this.activeProvider.set(undefined);
    this.activeComponent.set(null);
    this.results.set(null);
    this.query.set('');
  }

  applyHistoryEntry(entry: OmniSearchHistoryEntry): void {
    this.historyReplayEntryId.set(entry.id);
    this.query.set(entry.query);
    this.results.set(this._cloneResultsForReplay(entry.results));
    this._inputFocused.set(false);
    this._blurInputSoon();
  }

  removeHistoryEntry(entry: OmniSearchHistoryEntry, ev: Event): void {
    ev.stopPropagation();
    const p = this.activeProvider();
    p?.searchHistory?.removeEntry(entry.id);
    if (this.historyReplayEntryId() === entry.id) {
      this.historyReplayEntryId.set(null);
      this.results.set(null);
    }
  }

  // --- private helpers ---

  /** True when focus is inside the omni bar or a palette, results list, or loading state is shown. */
  private _isOmniSearchActive(): boolean {
    return (
      this._isFocusWithinOmniRoot() ||
      this.showPalette() ||
      this.showSuggestions() ||
      this.showRecentSearches() ||
      this.results() !== null ||
      this.loading()
    );
  }

  /** Whether keyboard focus is on the input or another control inside `#omniSearchRoot`. */
  private _isFocusWithinOmniRoot(): boolean {
    const root = this.omniSearchRoot?.nativeElement;
    const active = document.activeElement;
    return !!(root && active instanceof Node && root.contains(active));
  }

  /**
   * Escape (and full dismiss): blur the input, clear the query, close all panels/results.
   * Multi-provider mode also clears the provider chip; single-provider keeps the auto-selected chip.
   */
  private _dismissOmniSearchOnEscape(): void {
    this._inputFocused.set(false);
    this.showPalette.set(false);
    this.showSuggestions.set(false);
    this.highlightedIndex.set(-1);
    this.historyReplayEntryId.set(null);
    this.results.set(null);
    this.query.set('');
    this.loading.set(false);
    if (!this.singleProvider()) {
      this.activeProvider.set(undefined);
      this.activeComponent.set(null);
    }
    this._blurInputSoon();
  }

  /** Sync results/history when a provider is chosen (debounced input may not have run yet). */
  private _afterProviderSelected(): void {
    this.showPalette.set(false);
    this.showSuggestions.set(false);
    if (!this.query().trim()) {
      this.results.set(null);
      this.historyReplayEntryId.set(null);
    }
  }

  private _handleDocumentClick(event: MouseEvent) {
    const root = this.omniSearchRoot?.nativeElement;
    if (!root) return;

    if (this._isClickTreatedAsInsideOmniSearch(event, root)) {
      return;
    }

    this._inputFocused.set(false);
    this.showPalette.set(false);
    this.showSuggestions.set(false);
    this.highlightedIndex.set(-1);
    this.clearSearch();
    this._blurInputSoon();
  }

  /**
   * Outside-dismiss must ignore portaled overlays (PrimeNG `appendTo="body"`) that are still part
   * of the omni experience; `event.target` alone misses those because they are not DOM descendants
   * of `#omniSearchRoot`.
   *
   * @remarks Overlays under `body` should include a node matching {@link OMNI_SEARCH_PORTALED_OVERLAY_SELECTOR}
   * so clicks are recognized via `composedPath()` when the overlay is not under `#omniSearchRoot`.
   */
  private _isClickTreatedAsInsideOmniSearch(event: MouseEvent, root: HTMLElement): boolean {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
    for (const candidate of path) {
      if (candidate instanceof Node && root.contains(candidate)) {
        return true;
      }
      const el =
        candidate instanceof Element
          ? candidate
          : candidate instanceof Node
            ? candidate.parentElement
            : null;
      if (el?.closest(OMNI_SEARCH_PORTALED_OVERLAY_SELECTOR)) {
        return true;
      }
    }
    return false;
  }

  private async _handleInput(q: string) {
    const activeProvider = this.activeProvider();
    const providers = this._search.providers();

    // Trimmed query for easier checks
    const query = q.trim();

    // If no provider selected yet, keep palette visible
    if (!activeProvider) {
      // Always show palette while no provider selected
      this.showPalette.set(true);
      this.showSuggestions.set(false);

      // Only check shortcuts if query starts with '@'
      if (query.startsWith('@')) {
        const shortcutQuery = query.toLowerCase();
        const suggestions = providers.filter(
          (p: any) => p.shortcut && p.shortcut.toLowerCase().includes(shortcutQuery)
        );

        this.suggestedProviders.set(suggestions);
        this.showSuggestions.set(suggestions.length > 0);

        // ✅ auto-highlight nearest match (prefix match first)
        if (suggestions.length > 0) {
          // look for exact prefix match like @cl matches @client
          const prefixIndex = suggestions.findIndex((p: any) =>
            p.shortcut.toLowerCase().startsWith(shortcutQuery)
          );
          const newIndex = prefixIndex >= 0 ? prefixIndex : 0;
          this.highlightedIndex.set(newIndex);
        } else {
          this.highlightedIndex.set(-1);
        }

        // ✅ auto-select if exact full match typed
        const match = providers.find(
          (p: any) => p.shortcut?.toLowerCase() === shortcutQuery
        );
        if (match) {
          await this.selectProvider(match);
          this.query.set('');
          return;
        }
      }

      // Don’t run search yet — wait for provider selection
      this.results.set(null);
      return;
    }

    // --- Provider selected ---
    this.showPalette.set(false);
    this.showSuggestions.set(false);

    if (!query) {
      this.results.set(null);
      this.historyReplayEntryId.set(null);
    }
  }

  onSearch() {
    const provider = this.activeProvider();
    const q = this.query().trim();

    if (!provider || !q) return;

    this.loading.set(true);
    this.results.set(null);
    this.historyReplayEntryId.set(null);

    provider.search(q).subscribe({
      next: (results) => {
        this._amplitude.logEvent('omni_search_success', {
          provider: provider.label,
          query: q,
          results: results.length
        });
        this.results.set(results);
        provider.searchHistory?.recordSearch(q, results);
      },
      error: (err) => {
        this._amplitude.logEvent('omni_search_failed', {
          provider: provider.label,
          query: q,
          status: err.status
        });
        this.historyReplayEntryId.set(null);
        this.results.set([]);
      },
      complete: () => this.loading.set(false),
    });
  }

  private _cloneResultsForReplay(results: OmniSearchResult[]): OmniSearchResult[] {
    return results.map((r) => ({
      ...r,
      data: this._cloneReplayPayload(r.data),
    }));
  }

  /**
   * Clones provider result `data` for history replay: prefers {@link structuredClone}, then JSON,
   * then a shallow object/array copy; returns the original reference if every step fails so the
   * omni UI never breaks on non-cloneable payloads.
   */
  private _cloneReplayPayload<T>(data: T): T {
    if (typeof structuredClone === 'function') {
      try {
        return structuredClone(data);
      } catch {
        // e.g. functions, DOM nodes, non-cloneable proxies
      }
    }
    try {
      return JSON.parse(JSON.stringify(data)) as T;
    } catch {
      // e.g. circular refs, BigInt, non-JSON types
    }
    try {
      return this._shallowCloneForReplay(data);
    } catch {
      return data;
    }
  }

  /** One-level clone for plain objects and arrays; rethrows for exotic objects so callers can fall back. */
  private _shallowCloneForReplay<T>(value: T): T {
    if (value === null || typeof value !== 'object') {
      return value;
    }
    if (Array.isArray(value)) {
      return [...value] as T;
    }
    return { ...(value as object) } as T;
  }

  private _focusInputSoon() {
    // ensure DOM has settled before focusing
    setTimeout(() => this.searchInput?.nativeElement.focus(), 0);
  }

  private _blurInputSoon() {
    // ensure DOM has settled before focusing
    setTimeout(() => this.searchInput?.nativeElement.blur(), 0);
  }
}
